import React, { useState, useEffect } from "react";
import apiRequest from "../../../../../utils/ApiRequest/apiRequest.js";
import NoJumboCodes from "./NoJumboCodes.js";
import RetrieveCodesModal from "./RetrieveCodesModal.js";
import Pagination from "../../../../Reusable/Pagination/Pagination.js";
import config from "config";
import Dropdown from "../../../../Elements/Dropdown/Dropdown.js";
import Icon from "../../../../Elements/Icon/Icon.tsx";
import LoadingImageElement from "components/Elements/LoadingContainerElement/LoadingImageElement.tsx";
import { useContent } from "../../../../../contexts/contentContext/contentContext.js";
import { getDateFormatForInput } from "../../../../../utils/gridHelpers.js";
import { useModal } from "../../../../../contexts/modalContext/modalContext.js";
import { MaxBytesForFreeUsersUpload, tableCategories } from "./helpers.js";
import usePageInit from "hooks/usePageInit.ts";

import "../Profile.scss";

const JumboCodes = () => {
  const { setModal } = useModal();
  const { getTextByKey, getHtmlByKey, getPath } = useContent();
  const { isPageReady } = usePageInit();
  const [loading, setLoading] = useState(true);
  const [jumboCodes, setJumboCodes] = useState([]);
  const [numberOfCodes, setNumberOfCodes] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [fieldToOrder, setFieldToOrder] = useState("ConfirmCode_Used");
  const [OrderAscending, setOrderAscending] = useState(true);
  const maxCodesPerPage = 10;
  //list for filter status in server
  const statusUsed = [
    {
      enName: "All statuses",
      constName: getTextByKey("profilecodes-table-all-statuses"),
    },
    {
      enName: "Available",
      constName: getTextByKey("profilecodes-table-status-available"),
    },
    {
      enName: "Used",
      constName: getTextByKey("profilecodes-table-status-used"),
    },
  ];
  const [selectedStatus, setSelectedStatus] = useState(statusUsed[0]);

  //list for display filter status
  const statusUsedDisplay = [
    getTextByKey("profilecodes-table-all-statuses"),
    getTextByKey("profilecodes-table-status-available"),
    getTextByKey("profilecodes-table-status-used"),
  ];

  //set the link to download page
  const replaceLink = (link) => {
    let newLink = link.replace("#BaseUrl#", config.baseurl + `${getPath()}`);
    return newLink;
  };

  //display body table
  const displayCodes = jumboCodes?.map((item) => {
    return (
      <div className="table-body" key={item.ConfirmCode_Code}>
        <div className={`code-id ${item.ConfirmCode_Used ? "code-used" : ""}`}>
          <span className="code-id-box">{item.ConfirmCode_Code}</span>
        </div>
        <div className="code-status">
          <span>
            {item.ConfirmCode_Used === true
              ? getTextByKey("profilecodes-table-status-used")
              : parseInt(item.ConfirmCode_Size_Limit) >
                MaxBytesForFreeUsersUpload
              ? getTextByKey("profilecodes-table-status-available")
              : getTextByKey("profilecodes-table-status-canceled")}
          </span>
        </div>
        <div className="code-purchase">
          <span>{getDateFormatForInput(item.ConfirmCode_Create_Date)}</span>
        </div>
        <div className="code-usage">
          <span>
            {item.ConfirmCode_Date_Used === null
              ? "-"
              : getDateFormatForInput(item.ConfirmCode_Date_Used)}
          </span>
        </div>
        <div className="code-usage-type">
          <span>
            {item.ConfirmCode_Type === "ONE_TIME"
              ? item.ConfirmCode_Date_Used === null
                ? "-"
                : getTextByKey("profile-codes-upload-usage")
              : getTextByKey("code-usage-title")}
          </span>
        </div>
        <div className="code-mail-refferal">
          {item.ConfirmCode_Mail_Session_Id && (
            <a
              target="_blank"
              rel="noopener noreferrer"
              href={
                item.ConfirmCode_Mail_Session_Id
                  ? replaceLink(item.ConfirmCode_Mail_Session_Id)
                  : ""
              }
            >
              <i className="fas fa-external-link-alt"></i>
            </a>
          )}
        </div>
      </div>
    );
  });

  //re render if change page
  useEffect(() => {
    getJumboCodesByStatus(selectedStatus);
  }, [currentPage, fieldToOrder, OrderAscending]);

  //get codes by filtering status
  const getJumboCodesByStatus = async (status) => {
    const parameters = {
      field: fieldToOrder,
      order: OrderAscending,
      status: status.enName,
      page: currentPage === 1 ? 0 : (currentPage - 1) * maxCodesPerPage,
    };
    const response = await apiRequest(
      "UploadCodes",
      "GetUserUploadCodesByStatus",
      parameters
    );
    if (response.status === "success") {
      if (response.data) {
        if (
          status &&
          status.enName !== "All statuses" &&
          response.data.UploadCodesList === null
        ) {
          setJumboCodes([]);
          setNumberOfCodes(0);
          setLoading(false);
        } else {
          setJumboCodes(response.data.UploadCodesList);
          setLoading(false);
          setNumberOfCodes(response.data.TotalCodesCount);
        }
      }
    }
  };

  //upper the num of pages
  function up(v, n) {
    return (
      Math.ceil(v * Math.pow(maxCodesPerPage, n)) / Math.pow(maxCodesPerPage, n)
    );
  }

  //set the link to recover codes to mail
  useEffect(() => {
    if (!loading) {
      let element = document.getElementsByClassName("click-here");
      if (element !== null && element !== undefined && element.length > 0) {
        element[0].addEventListener(
          "click",
          () => setModal(() => <RetrieveCodesModal />),
          false
        );
      }
    }
  });

  //set status for filtering
  const handleChange = (e, itemKey) => {
    setSelectedStatus(statusUsed[itemKey]);
    setCurrentPage(1);
    getJumboCodesByStatus(statusUsed[itemKey]);
  };

  const sortByField = async (field, order) => {
    setLoading(true);
    const parameters = {
      field: field,
      order: order,
      status: selectedStatus ? selectedStatus.enName : null,
      page: currentPage === 1 ? 0 : (currentPage - 1) * maxCodesPerPage,
    };
    const response = await apiRequest(
      "UploadCodes",
      "GetCodesOrderByField",
      parameters
    );
    if (response.status === "success") {
      if (response.data) {
        setJumboCodes(response.data.UploadCodesList);
        setLoading(false);
        setNumberOfCodes(response.data.TotalCodesCount);
      }
    }
  };

  if (!isPageReady) {
    return null;
  }

  return (
    <div className="main-section-jumbocodes">
      {loading ? (
        <LoadingImageElement />
      ) : (
        <>
          <div className="header-container">
            <h1>{getTextByKey("profilecodes-title")}</h1>
            <div className="success-alert"></div>
          </div>

          {jumboCodes !== null ? (
            <>
              <div className="subtitle">
                <div>
                  <div>{getHtmlByKey("profilecodes-recover-codes-link")}</div>
                </div>
              </div>
              <div className="profile-table codes">
                <div className="subtitle-div">
                  <h2>{getTextByKey("profilecodes-sub-title")}</h2>{" "}
                  <div className="choose-status">
                    {getTextByKey("profilecodes-table-display")}:{" "}
                    <Dropdown
                      className="dropdown-statuses"
                      title={statusUsedDisplay[0]}
                      handleChange={handleChange}
                      selectList={statusUsedDisplay}
                      //defaultValue={list[shortList.indexOf(getLanguageCulture())]}
                    >
                      {" "}
                      <div className="">
                        <Icon name="arrow-down-solid" small />
                      </div>
                    </Dropdown>
                  </div>
                </div>
                {!loading && (
                  <div className="table-headers">
                    {tableCategories.map((item, i) => {
                      return (
                        <div key={i} className={item.className}>
                          <span>
                            {getTextByKey(
                              `profilecodes-table-header-${item.constKey}`
                            )}
                          </span>
                          {item.className !== "code-id" &&
                          item.className !== "code-usage-type" ? (
                            <Icon
                              iconClass="arrow-order"
                              name={
                                item.fieldName === fieldToOrder
                                  ? OrderAscending
                                    ? "arrow-up"
                                    : "arrow-down"
                                  : "arrow-down"
                              }
                              onClick={() => {
                                sortByField(item.fieldName, !OrderAscending);
                                setFieldToOrder(item.fieldName);
                                setOrderAscending((prev) => !prev);
                              }}
                            />
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                )}
                {displayCodes}
                {loading === true ? <LoadingImageElement /> : ""}
              </div>
              {loading !== true ? (
                <Pagination
                  maxPages={
                    numberOfCodes > 0
                      ? up(numberOfCodes / maxCodesPerPage, 0)
                      : 1
                  }
                  currentPage={currentPage}
                  setCurrentPage={setCurrentPage}
                />
              ) : (
                ""
              )}
            </>
          ) : (
            <NoJumboCodes />
          )}
        </>
      )}
    </div>
  );
};

export default JumboCodes;
